<template>
  <span @keydown.enter.prevent="">
    <v-autocomplete
      :value="value"
      :label="label"
      :placeholder="placeholder"
      multiple
      no-filter
      :disabled="disabled"
      :value-comparator="valueComparator"
      :items="filteredItems"
      chips
      :item-text="actualItemText"
      :item-value="actualItemValue"
      class="select-field-multiple-custom-main-container"
      :menu-props="{
        contentClass: 'select-field-multiple-custom-content',
        closeOnContentClick: false,
      }"
      box
      :hint="hint"
      :persistent-hint="hint"
      :error-messages="getErrors(validator)"
      :search-input.sync="search"
      @input="$emit('input', $event) && validator && validator.$touch()"
      @blur="validator && validator.$touch()"
      @change="$emit('change', $event) && validator && validator.$touch()"
    >
      <template slot="prepend-item">
        <v-list-tile ripple @click="toggleSelectAll">
          <v-list-tile-action>
            <v-icon>
              {{ selectAllIcon }}
            </v-icon>
          </v-list-tile-action>
          <v-list-tile-content>
            <v-list-tile-title>{{ adminText(&quot;shellAll.select_all&quot;, &quot;Select All&quot;) }}</v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
        <v-divider class="mt-2" />
      </template>

      <template slot="selection" slot-scope="{ item, index }">
        <template v-if="isAllItemsSelected">
          <span v-if="index === 0" class="select-field-multiple-custom-text">{{ allLabel === 'All' ? adminText('shellAll.all', 'All') : allLabel }}</span>
        </template>
        <template v-else>
          <v-chip v-if="index < maxVisibleSelections" class="select-field-multiple-custom-chip">
            <span>{{ item[actualItemText] || item }}</span>
          </v-chip>
          <span
            v-else-if="index === maxVisibleSelections && selectedCountExceedsLimit"
            class="select-field-multiple-custom-text"
          >
            …
          </span>
        </template>
      </template>
    </v-autocomplete>
  </span>
</template>

<script>
import AdminContext from '@/admin-i18n/context'
import { defaultMessages, getValidationErrors } from '@/utils'

export default {
  mixins: [AdminContext],
  name: 'select-with-all',
  props: {
    disabled: Boolean,
    placeholder: {
      type: String,
      default: null,
    },
    chips: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: null,
    },
    value: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    sections: {
      type: Array,
      default: null,
    },
    // Align with other components: prefer itemRender/itemSelect, but accept legacy itemText/itemValue
    itemRender: {
      type: String,
      default: null,
    },
    itemSelect: {
      type: String,
      default: null,
    },
    itemText: {
      type: String,
      default: null,
    },
    itemValue: {
      type: String,
      default: null,
    },
    validator: {
      type: Object,
      default: null,
    },
    errorMessages: {
      type: Object,
      default: null,
    },
    valueComparator: {
      type: Function,
      default: undefined,
    },
    hint: {
      type: String,
      default: undefined,
    },
    allLabel: {
      type: String,
      default: 'All',
    },
    maxVisibleSelections: {
      type: Number,
      default: 3,
    },
  },
  data() {
    return {
      search: '',
    }
  },
  computed: {
    hasSections() {
      return Array.isArray(this.sections) && this.sections.length > 0
    },
    normalizedSections() {
      if (!this.hasSections) {
        return []
      }

      return this.sections
        .map(section => ({
          title: section.title,
          items: Array.isArray(section.items) ? section.items : [],
        }))
        .filter(section => section.items.length > 0)
    },
    selectableItems() {
      if (this.hasSections) {
        return this.normalizedSections.flatMap(section => section.items)
      }

      return this.items
    },
    filteredItems() {
      if (!this.hasSections) {
        if (!this.search) {
          return this.items
        }

        return this.items.filter(item => this.matchesSearch(item))
      }

      const normalizedSearch = (this.search == null ? '' : String(this.search)).toLowerCase()

      return this.normalizedSections.flatMap(section => {
        const items = !normalizedSearch
          ? section.items
          : section.items.filter(item => this.matchesSearch(item))

        if (!items.length) {
          return []
        }

        return [
          { header: section.title },
          ...items,
        ]
      })
    },
    itemsAreObjects() {
      const list = Array.isArray(this.selectableItems) ? this.selectableItems : []
      if (list.length === 0) return false

      return list.every(it => it !== null && typeof it === 'object')
    },
    actualItemText() {
      // For primitive lists (e.g., strings), disable item-text so Vuetify uses the item itself
      if (!this.itemsAreObjects) return undefined

      return this.itemRender || this.itemText || 'name'
    },
    actualItemValue() {
      // For primitive lists (e.g., strings), disable item-value so Vuetify uses the item itself
      if (!this.itemsAreObjects) return undefined

      return this.itemSelect || this.itemValue || 'id'
    },
    selectedCountExceedsLimit() {
      const current = Array.isArray(this.value) ? this.value : []

      return current.length > this.maxVisibleSelections
    },
    isAllItemsSelected() {
      const current = Array.isArray(this.value) ? this.value : []

      return this.selectableItems.length > 0 && current.length === this.selectableItems.length
    },
    isAllSelected() {
      if (!this.filteredSelectableItems.length) return false
      const selected = Array.isArray(this.value) ? this.value : []
      const filteredValues = this.filteredSelectableItems.map(this.getItemValue)

      return filteredValues.every(val => selected.includes(val))
    },
    isPartiallySelected() {
      if (!this.filteredSelectableItems.length) return false
      const selected = Array.isArray(this.value) ? this.value : []
      const filteredValues = this.filteredSelectableItems.map(this.getItemValue)
      const selectedCount = filteredValues.filter(val => selected.includes(val)).length

      return selectedCount > 0 && selectedCount < this.filteredSelectableItems.length
    },
    filteredSelectableItems() {
      return this.filteredItems.filter(item => !(item && item.header))
    },
    selectAllIcon() {
      if (this.isAllSelected) return 'mdi-checkbox-marked'
      if (this.isPartiallySelected) return 'mdi-minus-box'

      return 'mdi-checkbox-blank-outline'
    },
  },
  methods: {
    getErrors: function (field) {
      return getValidationErrors({
        ...this.adminValidationMessages(defaultMessages),
        ...this.adminFieldValidationMessages(this.errorMessages),
      })(field)
    },
    matchesSearch(item) {
      const itemText = this.itemsAreObjects ? item[this.actualItemText] : item
      const searchLower = (this.search == null ? '' : String(this.search)).toLowerCase()

      return String(itemText == null ? '' : itemText)
        .toLowerCase()
        .includes(searchLower)
    },
    getItemValue(item) {
      const key = this.actualItemValue
      if (key && item !== null && typeof item === 'object') {
        return item[key]
      }

      return item
    },
    toggleSelectAll() {
      let next
      const selected = Array.isArray(this.value) ? this.value : []
      const filteredValues = this.filteredSelectableItems.map(this.getItemValue)

      if (this.isAllSelected) {
        // Deselect all filtered items
        next = selected.filter(val => !filteredValues.includes(val))
      } else {
        // Select all filtered items
        const newValues = filteredValues.filter(val => !selected.includes(val))
        next = [...selected, ...newValues]
      }

      this.$emit('input', next)
      this.$emit('change', next)
      this.validator && this.validator.$touch()
    },
  },
}
</script>

<style>
.select-field-multiple-custom-content .v-select-list {
  width: 100% !important;
  max-width: 692px !important;
}

.select-field-multiple-custom-content .v-list__tile {
  height: auto;
  margin: 10px 0;
}

.select-field-multiple-custom-content .v-list__tile__content {
  overflow: initial;
  flex: initial;
  height: auto;
  padding: 0;
  margin: 0;
  display: block;
}

.select-field-multiple-custom-main-container .v-select__selections {
  margin: 0 !important;
  min-height: unset !important;
}

.select-field-multiple-custom-content .v-list__tile__title {
  text-overflow: initial;
  overflow: initial;
  white-space: initial;
  height: auto;
  line-height: initial;
  position: relative;
}

.select-field-multiple-custom-content .v-card {
  width: auto;
}

.select-field-multiple-custom-chip .v-chip__content {
  text-overflow: initial;
  overflow: initial;
  white-space: initial;
  height: auto;
  line-height: initial;
  position: relative;
  margin: 5px 0;
  overflow-wrap: anywhere !important;
}

.select-field-multiple-custom-text {
  color: rgba(0, 0, 0, 0.87);
}
</style>
